import type { UserModel } from "models/UserModel";
import type { TLoginDetails } from "utils/gainsight";

import { fetchCsrfToken } from "api/csrfToken";
import { getUser } from "api/user";
import { compiledClientConfig } from "config";
import constate from "constate";
import { useAsyncImpact } from "hooks/useAsyncImpact";
import { useCallback, useRef, useState } from "react";
import { setCsrfToken } from "utils/csrf";

const useAuthenticatedUser = () => {
  const [user, setUser] = useState<null | UserModel>(null);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(true);
  const loginDetails = useRef<TLoginDetails>(undefined);
  const [csrfTokenInitialized, setCsrfTokenInitialized] = useState(false);

  const isLoggedIn = user != null;

  useAsyncImpact(async () => {
    if (user) {
      const token = await fetchCsrfToken();
      setCsrfToken(token);
      setCsrfTokenInitialized(true);
    }
  }, [user]);

  const updateUser = useCallback((newUser: UserModel) => {
    const mutatedUsersArray = (compiledClientConfig.mutatedUsers || "").split(",");
    if (mutatedUsersArray.includes(newUser.email)) {
      const mutatedUser = newUser
        .set("familyName", newUser.familyName + " 🏳️‍🌈")
        .set("givenName", "🏳️‍🌈 " + newUser.givenName);
      setUser(mutatedUser);
    } else {
      setUser(newUser);
    }
  }, []);

  const loadUser = useCallback(
    async (newLoginDetails?: TLoginDetails) => {
      setError(null);
      try {
        setLoading(true);
        const newUser = await getUser();
        updateUser(newUser);
        if (newLoginDetails) {
          loginDetails.current = newLoginDetails;
        }
      } catch (error) {
        setError(error as Error);
      } finally {
        setLoading(false);
      }
    },
    [updateUser],
  );

  const clearUser = useCallback(() => {
    setUser(null);
    setError(null);
  }, []);

  return {
    actions: { clearUser, loadUser, updateUser },
    state: { csrfTokenInitialized, error, isLoggedIn, loading, loginDetails: loginDetails.current, user },
  };
};

export const [AuthenticatedUserProvider, useAuthenticatedUserContext] = constate(useAuthenticatedUser);
